import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldStar, Buildings, SignOut, ArrowSquareOut, CurrencyCircleDollar } from "@phosphor-icons/react";

export default function ControlPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [pForm, setPForm] = useState({ price_inr: "", price_usd: "", gst_percent: "", plan_name: "" });
  const [drafts, setDrafts] = useState({});
  const [savingP, setSavingP] = useState(false);
  const [tab, setTab] = useState("orgs");

  const load = () => {
    api.get("/admin/orgs").then((r) => setOrgs(r.data));
    api.get("/admin/pricing").then((r) => {
      setPricing(r.data);
      setPForm({ price_inr: r.data.prices?.INR ?? "", price_usd: r.data.prices?.USD ?? "",
        gst_percent: r.data.gst_percent ?? "", plan_name: r.data.plan_name ?? "" });
    });
  };
  useEffect(load, []);

  const doLogout = async () => { await logout(); nav("/control/login", { replace: true }); };

  const savePricing = async () => {
    setSavingP(true);
    try {
      await api.patch("/admin/pricing", {
        price_inr: pForm.price_inr === "" ? null : Number(pForm.price_inr),
        price_usd: pForm.price_usd === "" ? null : Number(pForm.price_usd),
        gst_percent: pForm.gst_percent === "" ? null : Number(pForm.gst_percent),
        plan_name: pForm.plan_name || null,
      });
      toast.success("Pricing updated"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingP(false); }
  };

  const setDraft = (id, o) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...o } }));

  const saveOrg = async (org, patch) => {
    try { await api.patch(`/admin/orgs/${org.id}`, patch); toast.success("Organization updated");
      setDrafts((d) => { const n = { ...d }; delete n[org.id]; return n; }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveOverride = (org) => {
    const d = drafts[org.id] || {}; const patch = {};
    if (d.override_inr === "") patch.clear_override_inr = true;
    else if (d.override_inr !== undefined) patch.price_override_inr = Number(d.override_inr);
    if (d.override_usd === "") patch.clear_override_usd = true;
    else if (d.override_usd !== undefined) patch.price_override_usd = Number(d.override_usd);
    saveOrg(org, patch);
  };

  const toggleStatus = (org) =>
    saveOrg(org, { subscription_status: org.subscription_status === "active" ? "inactive" : "active" });

  const enterEnv = async (org) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${org.id}`);
      toast.success(`Entering ${data.org_name}…`);
      window.location.href = "/";  // full reload as the org owner
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const Money = ({ c, v }) => <span className="font-mono">{c}{Number(v || 0).toLocaleString()}</span>;

  return (
    <div className="min-h-screen bg-[#0B0D10] text-stone-200"
      style={{ backgroundImage: "radial-gradient(1000px 400px at 100% -10%, rgba(194,65,12,0.12), transparent)" }}>
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0F1216]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#C2410C] grid place-items-center">
              <ShieldStar size={18} weight="fill" className="text-white" />
            </span>
            <div>
              <div className="font-head font-semibold leading-tight">Inspiro Control</div>
              <div className="text-[11px] text-stone-400 uppercase tracking-wider">Super-Admin Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-stone-400 hidden sm:block">{user?.email}</span>
            <Button data-testid="control-signout" onClick={doLogout} variant="outline" size="sm"
              className="rounded-full gap-1.5 bg-transparent border-white/15 text-stone-200 hover:bg-white/5">
              <SignOut size={15} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-center gap-2">
          {[["orgs", "Organizations", Buildings], ["pricing", "Pricing", CurrencyCircleDollar]].map(([k, lbl, Icon]) => (
            <button key={k} data-testid={`control-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${
                tab === k ? "bg-[#C2410C] text-white" : "bg-white/5 text-stone-300 hover:bg-white/10"}`}>
              <Icon size={16} /> {lbl}
            </button>
          ))}
        </div>

        {tab === "pricing" && (
          <div className="bg-[#14171C] border border-white/10 rounded-2xl p-6">
            <h3 className="font-head font-semibold mb-1">Standard Plan Pricing</h3>
            <p className="text-xs text-stone-400 mb-5">USD is a fixed price point (not an FX conversion). GST applies to INR only.</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[["price_inr", "INR / month (₹)"], ["price_usd", "USD / month ($)"], ["gst_percent", "GST % (INR only)"], ["plan_name", "Plan name"]].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wider text-stone-400">{lbl}</label>
                  <Input data-testid={`control-pricing-${k}`} type={k === "plan_name" ? "text" : "number"}
                    value={pForm[k]} onChange={(e) => setPForm({ ...pForm, [k]: e.target.value })}
                    className="mt-1 rounded-xl bg-[#0F1216] border-white/10 text-stone-100" />
                </div>
              ))}
            </div>
            <Button data-testid="control-pricing-save" onClick={savePricing} disabled={savingP}
              className="mt-5 rounded-full bg-[#C2410C] hover:bg-[#9A3412]">{savingP ? "Saving…" : "Save Pricing"}</Button>
          </div>
        )}

        {tab === "orgs" && (
          <div className="bg-[#14171C] border border-white/10 rounded-2xl p-6">
            <h3 className="font-head font-semibold mb-5 flex items-center gap-2">
              <Buildings size={18} /> Organizations ({orgs.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-white/10">
                    <th className="py-2 pr-3">Organization</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Free</th>
                    <th className="py-2 pr-3">Override ₹</th>
                    <th className="py-2 pr-3">Override $</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {orgs.map((o) => {
                    const d = drafts[o.id] || {};
                    return (
                      <tr key={o.id} data-testid={`control-org-row-${o.id}`}>
                        <td className="py-3 pr-3">
                          <div className="font-medium text-stone-100">{o.name}</div>
                          <div className="text-xs text-stone-500">{o.owner_email} · {o.members} member(s)</div>
                        </td>
                        <td className="py-3 pr-3">
                          <button data-testid={`control-org-status-${o.id}`} onClick={() => toggleStatus(o)}
                            className={`rounded-full px-2.5 py-0.5 text-xs border ${
                              o.subscription_status === "active"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                : "bg-white/5 text-stone-400 border-white/10"}`}>
                            {o.subscription_status}
                          </button>
                          {o.currency && <span className="ml-1 text-xs text-stone-500">{o.currency}</span>}
                        </td>
                        <td className="py-3 pr-3">
                          <input type="checkbox" data-testid={`control-org-free-${o.id}`} checked={o.is_free}
                            onChange={(e) => saveOrg(o, { is_free: e.target.checked })} />
                        </td>
                        <td className="py-3 pr-3">
                          <Input data-testid={`control-org-override-inr-${o.id}`} type="number"
                            placeholder={String(pricing?.prices?.INR ?? "")}
                            value={d.override_inr !== undefined ? d.override_inr : (o.price_override?.INR ?? "")}
                            onChange={(e) => setDraft(o.id, { override_inr: e.target.value })}
                            className="rounded-lg h-9 w-28 text-xs bg-[#0F1216] border-white/10 text-stone-100" />
                        </td>
                        <td className="py-3 pr-3">
                          <Input data-testid={`control-org-override-usd-${o.id}`} type="number"
                            placeholder={String(pricing?.prices?.USD ?? "")}
                            value={d.override_usd !== undefined ? d.override_usd : (o.price_override?.USD ?? "")}
                            onChange={(e) => setDraft(o.id, { override_usd: e.target.value })}
                            className="rounded-lg h-9 w-24 text-xs bg-[#0F1216] border-white/10 text-stone-100" />
                        </td>
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" data-testid={`control-org-save-${o.id}`}
                              onClick={() => saveOverride(o)}
                              className="rounded-full text-xs bg-transparent border-white/15 text-stone-200 hover:bg-white/5">Save</Button>
                            <Button size="sm" data-testid={`control-org-enter-${o.id}`} onClick={() => enterEnv(o)}
                              className="rounded-full text-xs bg-[#C2410C] hover:bg-[#9A3412] gap-1.5">
                              <ArrowSquareOut size={14} /> Enter
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-stone-500">Click the status pill to toggle Active/Inactive. Blank override = list price. “Free” comps an internal org. “Enter” logs you into that org’s environment.</p>
          </div>
        )}
      </main>
    </div>
  );
}
